import { useEffect, useRef } from "react";
import { FileText, Bot, Newspaper, Scissors, ImageDown, ArrowUpRight, ExternalLink } from "lucide-react";

const SERVICES = [
  {
    icon: FileText,
    title: "Invoice Reader API",
    desc: "Extract structured data from invoices, receipts, and documents instantly. Our OCR API handles any format with high accuracy — ready for your workflow in minutes.",
    tag: "API",
    link: "https://ocr.tyosolution.com",
    linkLabel: "Try Demo",
    featured: true,
  },
  {
    icon: Bot,
    title: "ScoreMyCV — ATS CV Optimizer",
    desc: "Upload your CV and a job description to get an instant ATS score, keyword analysis, AI-powered rewrite, and skill gap recommendations — all in seconds.",
    tag: "AI",
    link: "https://scoremycv.tyosolution.com",
    linkLabel: "Try ScoreMyCV",
    featured: false,
  },
  {
    icon: Scissors,
    title: "PDF Splitter",
    desc: "Split any PDF by page range or interval in seconds. Privacy-first: files are deleted instantly after processing. Pay-per-use credits, never expire.",
    tag: "API",
    link: "https://pdf-splitter.tyosolution.com",
    linkLabel: "Try PDF Splitter",
    featured: false,
  },
  {
    icon: ImageDown,
    title: "Image Optimizer API",
    desc: "Convert to WebP, compress, and batch-process images via API. Privacy-first — files never stored. Sub-second processing for high-resolution images, handles up to 500 images per batch.",
    tag: "API",
    link: "https://img.tyosolution.com",
    linkLabel: "Try Image Optimizer",
    featured: false,
  },
  {
    icon: Newspaper,
    title: "News & Update Tracking",
    desc: "Automated monitoring of industry news, Google Core Updates, and algorithm changes delivered to you in real time via Slack or email.",
    tag: "SOON",
    link: null,
    linkLabel: "Join Waitlist",
    featured: false,
  },
];

export default function Services() {
  const sectionRef = useRef(null);
  const scrollToContact = () =>
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.target.classList.toggle("visible", e.isIntersecting)),
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => el && observer.unobserve(el);
  }, []);

  return (
    <section id="services" className="py-24 md:py-32 px-6 md:px-10 section-dark" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div ref={sectionRef} className="section-reveal text-center mb-14">
          <span className="overline-tag block mb-3">What We Build</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            Five services. One platform.
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-xl mx-auto leading-relaxed">
            Purpose-built automation tools for modern businesses — deploy in minutes, scale with confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="services-grid">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className={`glass-card p-7 group cursor-pointer relative overflow-hidden ${
                  service.featured ? "border-[#38BDF8]/25 bg-[#38BDF8]/[0.04]" : ""
                }`}
                style={{ animationDelay: `${i * 0.08}s` }}
                data-testid={`service-card-${i}`}
              >
                {service.featured && (
                  <div className="absolute top-0 left-0 right-0 h-0.5">
                    <div className="trace-beam h-full" />
                  </div>
                )}

                <div className="flex items-start justify-between mb-5">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    service.featured ? "bg-[#38BDF8]/20" : "bg-white/[0.06]"
                  } group-hover:bg-[#38BDF8]/20 transition-colors`}>
                    <Icon size={22} className="text-[#38BDF8]" />
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-[#38BDF8] bg-[#38BDF8]/10 border border-[#38BDF8]/20 px-2.5 py-1 rounded-full">
                    {service.tag}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-white mb-2.5">{service.title}</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed mb-5">{service.desc}</p>

                {service.link ? (
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[#38BDF8] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`service-link-${i}`}
                  >
                    {service.linkLabel} <ExternalLink size={13} />
                  </a>
                ) : (
                  <button
                    onClick={scrollToContact}
                    className="flex items-center gap-1.5 text-[#38BDF8] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`service-cta-${i}`}
                  >
                    {service.linkLabel} <ArrowUpRight size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
